import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import {
  updateWithOptimisticLock,
  OptimisticLockConflictError,
} from "@/lib/optimistic-lock";

/**
 * Tabeller som støttes av denne generiske hooken. Holdes smal slik at
 * typesystemet fanger feilstaver, og slik at vi kan styre cache-invalidering
 * eksplisitt per tabell.
 *
 * VIB-2531: `customers` er BEVISST fjernet herfra. Kundedata må gå via
 * `useUpdateCustomer` → `update-po-customer`, som pusher til PowerOffice før
 * den lagrer lokalt. En direkte skriving hit ville blitt stille overskrevet av
 * import-customers (pg_cron 04:30) neste morgen — det var nøyaktig bugen. Ved
 * å ta tabellen ut av unionen fanger tsc forsøk på å omgå write-through.
 */
export type UpdateFieldTable = "projects" | "users";

interface UpdateFieldArgs {
  table: UpdateFieldTable;
  id: string;
  field: string;
  value: unknown;
  /**
   * VIB-757: påkrevd når `table='projects'` for optimistic locking. For
   * andre tabeller ignoreres feltet (lavere konflikt-risiko, ikke
   * implementert ennå).
   */
  expectedUpdatedAt?: string;
}

/**
 * Generisk inline-edit-mutation for prosjekt/kunde/bruker-felt. Brukes
 * av `EditableInfoRow` og `EditableCustomerRow` for å oppdatere
 * enkeltkolonner uten å duplisere supabase-imports i komponentene
 * (CLAUDE.md regel 4).
 *
 * VIB-758: invalidering går via cache-registry.
 * VIB-757: prosjekt-tabellen har optimistic locking via
 * updateWithOptimisticLock. Andre tabeller bruker direkte update.
 */
export function useUpdateField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ table, id, field, value, expectedUpdatedAt }: UpdateFieldArgs) => {
      if (table === "projects") {
        if (!expectedUpdatedAt) {
          throw new Error(
            "expectedUpdatedAt er påkrevd for projects (VIB-757 optimistic locking)",
          );
        }
        await updateWithOptimisticLock(
          "projects",
          id,
          { [field]: value },
          expectedUpdatedAt,
        );
        return;
      }
      const { error } = await supabase
        .from(table)
        .update({ [field]: value })
        .eq("id", id);
      if (error) throw new Error(error.message);
    },
    onError: (err) => {
      if (err instanceof OptimisticLockConflictError) {
        toast.error(
          "En annen bruker har endret prosjektet. Last inn på nytt og prøv igjen.",
        );
      } else {
        toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
      }
    },
    onSettled: async (_data, _err, { table, id }) => {
      const context = table === "projects" ? { projectId: id } : { userId: id };
      await invalidateFor(qc, table, context);
    },
  });
}
